import { useState } from 'react';
import type { Anlagenpruefung, Anlagenvorschau, Anlagenzeile, ApiClient } from '../../api';
import { EinheitText, IconEinheit } from '../../EinheitIcons';
import {
  DataTable, Drawer, ErrorState, FormSection, PropertyGrid, StatusBadge, formatiereZahl, type Spalte,
} from '../../muster';
import { LUFTSTROM, istHydraulik } from './datenregeln';
import { Ablagefeld } from './LueftungDatenblatt';

type Einheit = Anlagenvorschau['einheiten'][number];
type Punkt = Einheit['punkte'][number] & { einheit: Einheit };
type Vorschauwert = Anlagenvorschau['werte'][number];

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HERSTELLERAUSLEGUNG EINER ANLAGE EINLESEN (Anlagenliste, Schubfach)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Anlage steht fest - sie kommt aus der Zeile. Drei Schritte: das JSON
 * prüfen (`anlagePruefen`), optional die beiden PDF dazulegen, Vorschau
 * (`anlageUebernehmen(…, false)`), dann übernehmen (`…, true[, überschreiben]`)
 * und die PDF je für sich ablegen (`anlageUnterlage`): Ein Fehler beim Ablegen
 * stellt die schon übernommenen Daten nicht in Frage und wird getrennt gemeldet.
 *
 * TV6d: aus `seiten/Anlagenliste.tsx` herausgelöst und auf die Muster gesetzt;
 * jeder Aufruf ist der alte (`Lueftungsdaten.schreibwege.test.tsx`). Neu: Die
 * beiden PDF-Felder nehmen wie die Ablage in der Zeile nur PDF an.
 */
export function Zuweisungsdialog({ api, projektId, anlage, schliessen }: {
  api: ApiClient;
  projektId: string;
  anlage: Anlagenzeile;
  schliessen: () => void;
}) {
  const [datei, setDatei] = useState<File | null>(null);
  const [pruefung, setPruefung] = useState<Anlagenpruefung | null>(null);
  const [datenblatt, setDatenblatt] = useState<File | null>(null);
  const [skizze, setSkizze] = useState<File | null>(null);
  const [vorschau, setVorschau] = useState<Anlagenvorschau | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<string | null>(null);

  const pruefen = async (d: File) => {
    setLaeuft(true);
    setFehler(null);
    setVorschau(null);
    try {
      const p = await api.anlagePruefen(d);
      setPruefung(p);
      setDatei(p.gueltig ? d : null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const rechnen = async (schreiben: boolean, ueberschreiben = false) => {
    if (!datei) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const a = await api.anlageUebernehmen(datei, projektId, anlage.id, schreiben, ueberschreiben);
      if (!schreiben) { setVorschau(a as Anlagenvorschau); return; }

      // Die Daten sind übernommen. Was jetzt noch kommt, ist zusätzlich - die PDF nach dem JSON, jede für sich.
      const meldungen = [a.message as string];
      const gescheitert: string[] = [];
      for (const [d, art, name] of [[datenblatt, 'datenblatt', 'Datenblatt'], [skizze, 'skizze', 'Geräteskizze']] as const) {
        if (!d) continue;
        try {
          const u = await api.anlageUnterlage(anlage.id, d, art);
          meldungen.push(u.message);
        } catch (e) {
          gescheitert.push(`${name}: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
      setFertig(meldungen.join(' '));
      // Getrennt gemeldet, nicht in die Erfolgsmeldung gemischt: Die Daten liegen UND eine Unterlage fehlt.
      setFehler(gescheitert.length > 0
        ? `Die Daten sind übernommen. Nicht abgelegt wurde: ${gescheitert.join('; ')}. `
          + 'Die Unterlagen lassen sich in der Anlagenliste jederzeit nachtragen.'
        : null);
      setVorschau(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  // Schubfach statt Modal (Abschnitt 16): drei Schritte mit Vorschau und mehreren Ablegefeldern.
  return (
    <Drawer
      offen
      titel={anlage.anlage}
      beschreibung={`${anlage.ebene ?? ''} · ${formatiereZahl(anlage.bauteile, 0)} Bauteile`}
      beiSchliessen={schliessen}
      breite="720px"
      fuss={fertig ? <button type="button" className="m-knopf m-knopf-haupt" onClick={schliessen}>Fertig</button> : undefined}
    >
      <FormSection titel="Herstellerauslegung">
        <Ablagefeld
          beschriftung={datei ? `${datei.name} - andere Datei wählen` : 'JSON hierher ziehen oder klicken'}
          laeuft={laeuft && !pruefung}
          laeuftText="wird geprüft …"
          endung=".json"
          akzeptiert=".json,application/json"
          falsch={'Nur JSON-Dateien im Format tuv-datasheet-import, Umfang „anlage".'}
          ablegen={(d) => void pruefen(d)}
          fehlerMelden={setFehler}
        />
      </FormSection>

      {pruefung && !pruefung.gueltig && (
        <ErrorState klein titel="Kein gültiges Anlagendokument." meldung={(pruefung.fehler ?? []).join(' · ')} />
      )}

      {pruefung?.gueltig && <Kopfdaten pruefung={pruefung} />}

      {pruefung?.gueltig && (
        <FormSection titel="Unterlagen als PDF" beschreibung="optional, jederzeit nachtragbar">
          <div className="lue-unterlagen">
            <Ablagefeld
              beschriftung={datenblatt ? `Datenblatt: ${datenblatt.name}`
                : anlage.hatDatenblatt ? `Datenblatt liegt: ${anlage.dateiname} - ersetzen` : 'Technisches Datenblatt (PDF)'}
              laeuft={false}
              ablegen={setDatenblatt}
              fehlerMelden={setFehler}
            />
            <Ablagefeld
              beschriftung={skizze ? `Skizze: ${skizze.name}`
                : anlage.hatSkizze ? `Skizze liegt: ${anlage.skizzeDateiname} - ersetzen` : 'Geräteskizze (PDF)'}
              laeuft={false}
              ablegen={setSkizze}
              fehlerMelden={setFehler}
            />
          </div>
          <p className="lue-leise">
            Beim Ablegen entsteht ein Verweis ohne Anmeldung - er landet in den Feldern „Link zu Herstellerunterlagen“ und
            „Link zu Geräteskizze“ und hält, weil er nicht die ablaufende Adresse des Objektspeichers ist.
          </p>
        </FormSection>
      )}

      {fehler && <ErrorState klein titel="Das hat nicht geklappt." meldung={fehler} />}
      <p className="lue-meldung" role="status">{fertig ?? ''}</p>

      {pruefung?.gueltig && !fertig && (
        <div className="lue-speichern">
          <button type="button" className="m-knopf" disabled={laeuft || !datei} onClick={() => void rechnen(false)}>
            Vorschau und prüfen
          </button>
        </div>
      )}

      {vorschau && <Vorschau vorschau={vorschau} laeuft={laeuft} uebernehmen={(u) => void rechnen(true, u)} />}
    </Drawer>
  );
}

// ═══ Kopfdaten und Vorschau ═════════════════════════════════════════════════

function Kopfdaten({ pruefung: p }: { pruefung: Anlagenpruefung }) {
  const hinweise = [
    ...(p.bereits && p.message ? [p.message] : []),
    // Bei einer Hydraulikstation sind Anlagenkapazität und Gehäuseluftstrom zwei Größen. Wer das gleichsetzt, hat eine
    // Anlage mit 3.000 m³/h in der Liste, obwohl sie 100.000 kann.
    ...(istHydraulik(p.produktklasse)
      ? [`Produktklasse ${p.produktklasse}: Anlagenkapazität ${formatiereZahl(p.nennkapazitaet, 0)} m³/h - der Luftstrom `
        + 'im Gehäuse ist eine andere Größe. Zuluft und Abluft werden deshalb nicht gefüllt.']
      : []),
  ];

  return (
    <section className="lue-abschnitt">
      <h3 className="lue-unterkopf">Geprüft</h3>
      <PropertyGrid
        spalten={3}
        dicht
        eintraege={[
          { beschriftung: 'Gerät', wert: p.geraetebezeichnung ?? null },
          { beschriftung: 'Hersteller', wert: [p.hersteller, p.baureihe].filter(Boolean).join(' ') || null },
          { beschriftung: 'Angebot', wert: p.angebotsnummer ?? null },
          { beschriftung: 'Angebotsdatum', wert: p.angebotsdatum ?? null },
          { beschriftung: 'Version', wert: p.dataVersion ?? null },
          { beschriftung: 'Luftströme', wert: p.luftstroeme ?? 0 },
          { beschriftung: 'Einheiten', wert: p.einheiten ?? 0 },
        ]}
      />
      {hinweise.map((h) => <p key={h} className="lue-leise"><StatusBadge klein zustand="info" text="Hinweis" /> {h}</p>)}
      {(p.warnungen ?? []).length > 0 && (
        <div className="lue-hinweisliste">
          <StatusBadge klein zustand="warnung" text="Hinweise des Erzeugers" />
          <ul>{p.warnungen!.map((w) => <li key={w}>{w}</li>)}</ul>
        </div>
      )}
    </section>
  );
}

function Vorschau({ vorschau: v, laeuft, uebernehmen }: {
  vorschau: Anlagenvorschau;
  laeuft: boolean;
  uebernehmen: (ueberschreiben: boolean) => void;
}) {
  const neu = v.werte.filter((x) => x.zustand === 'neu').length;
  const konflikte = v.werte.filter((x) => x.zustand === 'konflikt').length;
  const stroeme = [...new Set(v.einheiten.map((e) => e.luftstrom))];
  const punkte: Punkt[] = v.einheiten.flatMap((e) => (e.punkte ?? []).map((p) => ({ ...p, einheit: e })));

  const einheitSpalten: Spalte<Einheit>[] = [
    { schluessel: 'nr', titel: 'Nr', art: 'zahl', nachkomma: 0, wert: (e) => e.reihenfolge },
    {
      schluessel: 'art', titel: 'Art', immerSichtbar: true, wert: (e) => EinheitText[e.art] ?? e.art,
      // Eine Folge aus zwanzig Einheiten liest man nicht Wort für Wort, sondern erkennt sie am Sinnbild.
      inhalt: (e) => <span className="lue-einheit"><IconEinheit art={e.art} size={24} title={EinheitText[e.art] ?? e.art} /> {EinheitText[e.art] ?? e.art}</span>,
    },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (e) => e.bezeichnung },
    {
      schluessel: 'dp', titel: 'Δp', art: 'zahl', einheit: 'Pa', nachkomma: 0, wert: (e) => e.druckverlustPa,
      // Beim Ventilator steht der gelieferte Druck (+), er verbraucht keinen.
      inhalt: (e) => (e.art === 'VENT' && e.druckverlustPa ? `+${formatiereZahl(e.druckverlustPa, 0)}` : formatiereZahl(e.druckverlustPa, 0)),
    },
    { schluessel: 'werte', titel: 'Werte', art: 'zahl', nachkomma: 0, wert: (e) => e.werte },
  ];

  // Ein Change-Over führt Kühlen und Heizen mit verschiedenen Leistungen und Medientemperaturen - beide gehören gezeigt.
  const punktSpalten: Spalte<Punkt>[] = [
    { schluessel: 'register', titel: 'Register', immerSichtbar: true, wert: (p) => p.einheit.bezeichnung },
    { schluessel: 'betrieb', titel: 'Betriebsart', wert: (p) => p.modusDe },
    { schluessel: 'leistung', titel: 'Leistung', einheit: 'kW', ausrichtung: 'rechts', wert: (p) => p.leistung },
    {
      schluessel: 'vlrl', titel: 'VL / RL', einheit: '°C', ausrichtung: 'rechts',
      wert: (p) => (p.vorlauf !== null && p.ruecklauf !== null ? `${p.vorlauf} / ${p.ruecklauf}` : null),
    },
    { schluessel: 'werte', titel: 'Werte', art: 'zahl', nachkomma: 0, wert: (p) => p.werte },
    {
      schluessel: 'stimmig', titel: '', art: 'status', sortierbar: false, wert: (p) => (p.stimmig === false ? 'unstimmig' : ''),
      inhalt: (p) => (p.stimmig === false
        ? <StatusBadge klein zustand="warnung" text="unstimmig" titel="Die genannte Gesamtleistung entspricht nicht der Summe aus sensibler und latenter Leistung" />
        : null),
    },
  ];

  const wertSpalten: Spalte<Vorschauwert>[] = [
    { schluessel: 'feld', titel: 'Feld', immerSichtbar: true, wert: (x) => x.label },
    { schluessel: 'neu', titel: 'neu', wert: (x) => x.neu },
    {
      schluessel: 'bisher', titel: 'bisher', wert: (x) => (x.zustand === 'konflikt' ? x.bisher : x.zustand === 'unveraendert' ? 'gleich' : 'neu'),
      inhalt: (x) => (x.zustand === 'konflikt'
        ? <><StatusBadge klein zustand="warnung" text="Konflikt" /> {x.bisher}</>
        : <span className="lue-leise">{x.zustand === 'unveraendert' ? 'gleich' : 'neu'}</span>),
    },
    { schluessel: 'revit', titel: 'Revit-Parameter', wert: (x) => x.revitParameter ?? 'nur Datenbank' },
  ];

  return (
    <>
      <section className="lue-abschnitt">
        <h3 className="lue-unterkopf">Vorschau</h3>
        <PropertyGrid
          spalten={3}
          dicht
          eintraege={[
            { beschriftung: 'neue Werte', wert: neu },
            { beschriftung: 'Konflikte', wert: konflikte },
            { beschriftung: 'Einheiten', wert: v.einheiten.length },
            ...(v.betriebspunkte > 0 ? [{ beschriftung: 'Betriebspunkte', wert: v.betriebspunkte }] : []),
            ...v.druck.flatMap((d) => [
              { beschriftung: `${LUFTSTROM[d.luftstrom] ?? d.luftstrom} · intern`, wert: d.internPa, einheit: 'Pa', nachkomma: 0 },
              { beschriftung: `${LUFTSTROM[d.luftstrom] ?? d.luftstrom} · Ventilator`, wert: d.ventilatorPa, einheit: 'Pa', nachkomma: 0 },
            ]),
          ]}
        />

        {v.konflikt && <p className="lue-leise"><StatusBadge klein zustand="warnung" text="Konflikt" /> {v.konflikt}</p>}

        {/* Die Unstimmigkeiten des Herstellers werden nicht korrigiert - wer eine Herstellerangabe zurechtrechnet, hat
            danach eine eigene Angabe. Aber sie gehören gesehen: ein Punkt für die Rückfrage. */}
        {(v.unstimmigkeiten?.length ?? 0) > 0 && (
          <div className="lue-hinweisliste">
            <StatusBadge klein zustand="warnung" text={`${v.unstimmigkeiten.length} rechnerische Unstimmigkeit(en) beim Hersteller`} />
            <p className="lue-leise">
              Die genannte Gesamtleistung entspricht nicht der Summe aus sensibler und latenter Leistung. Die Werte bleiben
              unverändert - das ist eine Rückfrage an den Hersteller, keine Rechenaufgabe.
            </p>
            <ul>
              {v.unstimmigkeiten.map((u, n) => (
                <li key={`${u.einheit}-${n}`}>
                  <strong>{u.einheit}</strong> {u.betriebsart}: genannt <strong>{u.gesamt} kW</strong>, Summe {u.summe} kW
                  {' '}({u.sensibel} sensibel + {u.latent} latent)
                </li>
              ))}
            </ul>
          </div>
        )}

        {v.unbekannteArten.length > 0 && (
          <p className="lue-leise">
            <StatusBadge klein zustand="warnung" text="Unbekannte Einheitenarten" /> {v.unbekannteArten.join(', ')} - diese
            Einheiten werden übersprungen.
          </p>
        )}
      </section>

      {stroeme.map((strom) => {
        const liste = v.einheiten.filter((e) => e.luftstrom === strom).sort((a, b) => a.reihenfolge - b.reihenfolge);
        return (
          <DataTable
            key={strom}
            name={`${LUFTSTROM[strom] ?? strom} · ${formatiereZahl(liste.length, 0)} Einheiten`}
            spalten={einheitSpalten}
            zeilen={liste}
            schluessel={(e) => `${e.luftstrom}-${e.reihenfolge}`}
            suche={false}
            spaltenwahl={false}
            dicht
          />
        );
      })}

      {punkte.length > 0 && (
        <DataTable
          name={`Betriebspunkte der Register (${formatiereZahl(v.betriebspunkte, 0)})`}
          spalten={punktSpalten}
          zeilen={punkte}
          schluessel={(p) => `${p.einheit.componentKey}-${p.reihenfolge}`}
          suche={false}
          spaltenwahl={false}
          dicht
        />
      )}

      <details className="lue-aufklapp">
        <summary>Anlagendaten im Einzelnen ({formatiereZahl(v.werte.length, 0)})</summary>
        <DataTable
          name="Anlagendaten im Einzelnen"
          spalten={wertSpalten}
          zeilen={v.werte}
          schluessel={(x) => x.fieldKey}
          zeilenKlasse={(x) => (x.zustand === 'konflikt' ? 'datenfeld' : undefined)}
          dicht
        />
      </details>

      {v.merkmaleOhneFeld > 0 && (
        <p className="lue-leise">
          {formatiereZahl(v.merkmaleOhneFeld, 0)} Merkmal(e) haben kein Feld im Katalog. Sie gehen nicht verloren - sie
          werden mit ihrer Beschriftung im Quelltext der Einheit abgelegt.
        </p>
      )}

      <div className="lue-speichern">
        <button type="button" className="m-knopf m-knopf-haupt" disabled={laeuft || !!v.konflikt} onClick={() => uebernehmen(false)}>
          Version {v.dataVersion} zuweisen und übernehmen
        </button>
        {(v.konflikt || konflikte > 0) && (
          <button type="button" className="m-knopf" disabled={laeuft} onClick={() => uebernehmen(true)}>
            Mit Überschreiben übernehmen
          </button>
        )}
      </div>
      <p className="lue-leise">
        Von Hand gepflegte Werte bleiben unberührt. Die Funktionseinheiten werden ersetzt - eine Fassung beschreibt das
        Gerät vollständig.
      </p>
    </>
  );
}
